import {eq, isEmpty} from '@shared/helpers/lang'

import {
    AccountPayInfo,
    BankEasyPayMaintenanceStauts,
    BankEasyPayMaintenanceStautsType,
    PendingPaymentStatus,
} from '@web/core/constants'

import {AccountState} from '../interfaces'
import {AccountInfoView, AccountView} from '../interfaces/account'

export const getInitialState = (): AccountState => ({
    recharge: {
        accounts: [],
        selectedAccountKey: null,
    },
    bankEasyPay: {
        accounts: [],
        selectedAccountKey: null,
        billing: {
            pgCp: '',
            pgId: '',
            minimumAmount: null,
            maximumAmount: null,
        },
    },
    payServiceInfo: null,
    maintenanceInfo: null,
    naverBankbookBenefitOnRecentPayment: false,
    pendingPaymentInfo: {
        hasWaitingPaymentOrder: null,
        status: PendingPaymentStatus.DISABLE,
    },
})

/** 은행 결제 서비스 전체 점검 상태 */
const AccountAllMaintenanceStatus: BankEasyPayMaintenanceStautsType[] = [
    BankEasyPayMaintenanceStauts.ALL_BANK_INSPECT,
    BankEasyPayMaintenanceStauts.COMMON_INSPECT,
    BankEasyPayMaintenanceStauts.SERVICE_MAINTENANCE,
]

/** 은행 전체 점검 여부 */
export function isAllBanksUnAvailable(status: Maybe<BankEasyPayMaintenanceStautsType>): boolean {
    return !!status && AccountAllMaintenanceStatus.includes(status)
}

/** 네이버 통장 여부 */
export function isNaverAccount({payPartnershipItemCode}: AccountView): boolean {
    return eq(payPartnershipItemCode, 'MIRAEASSET_CMA')
}

/** 계좌 목록 정보 반환 */
export function getAccountsInfo(
    accounts: AccountView[],
    isPendingPaymentStatusEnable: boolean,
    isTotalMaintenance: boolean,
): {
    /** 비동기 결제 여부가 계산된 계좌 목록 정보 */
    accounts: AccountInfoView[]
    /** 계좌 등록 여부 */
    isRegistered: boolean
    /** 네이버 통장 보유 여부 */
    isNaverAccountRegistered: boolean
    /** 결제 가능한 계좌 있는지 여부 (동기, 비동기 무관) */
    hasPayableAccount: boolean
} {
    const refinedAccounts = accounts.map((account): AccountInfoView => {
        const {payable, pendingPayable, payServiceMaintenanceSchedule} = account

        const isMaintenance = isTotalMaintenance || !!payServiceMaintenanceSchedule
        const isPayable = !isMaintenance && payable
        const isPendingPayable = isPendingPaymentStatusEnable && pendingPayable
        const status = isPayable
            ? AccountPayInfo.PAY_AVAILABLE
            : isPendingPayable
              ? AccountPayInfo.PENDING_PAY_AVAILABLE
              : AccountPayInfo.PAY_UNABLE

        return {
            ...account,
            payInfo: {
                status,
                isPayable,
                isPendingPayable,
                isMaintenance,
            },
        }
    })
    return {
        accounts: refinedAccounts,
        isRegistered: !isEmpty(accounts),
        isNaverAccountRegistered: !isEmpty(accounts.filter(isNaverAccount)),
        hasPayableAccount: refinedAccounts.some(({payInfo}) => payInfo.status !== AccountPayInfo.PAY_UNABLE),
    }
}

/** 선택된 계좌 정보 반환 */
export function getAccountInfo({
    accounts,
    selectedAccountKey,
}: {
    accounts: AccountInfoView[]
    selectedAccountKey: Maybe<string>
}): {
    /** 선택한 계좌 정보 */
    selectedAccount: Maybe<AccountInfoView>
    /** 선택한 계좌 index */
    selectedAccountIndex: number
    /** 선택한 계좌가 네이버 통장인지 여부 */
    isSelectedNaverAccount: boolean
    /** 선택된 계좌 결제 가능 여부 (동기, 비동기 무관) */
    isSelectedAccountPayAvailable: boolean
} {
    const selectedAccountIndex = accounts.findIndex(({accountKey}) => selectedAccountKey === accountKey)

    const selectedAccount: Maybe<AccountInfoView> = accounts[selectedAccountIndex] ?? null
    return {
        selectedAccount,
        selectedAccountIndex,
        isSelectedNaverAccount: !!selectedAccount && isNaverAccount(selectedAccount),
        isSelectedAccountPayAvailable:
            !!selectedAccount && selectedAccount.payInfo.status !== AccountPayInfo.PAY_UNABLE,
    }
}
